import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { TranslocoModule } from '@jsverse/transloco';
import { AutoCompleteResult, AutoSearchSubType } from '@rma/generic/api/auto-search';
import { GoogleMapsScriptService } from '@rma/maps/api';
import type { SearchType } from '@rma/search/domain';
import { AutoCompleteControlComponent } from '@rma/search/public/ui-autocomplete';
import { take } from 'rxjs';
import { FindMyAgentStore } from '../orc-find-my-agent/find-my-agent.store';
import { AddressSearchStore } from './address-search.store';

@Component({
  selector: 'rma-timeline',
  imports: [AsyncPipe, MatButtonModule, AutoCompleteControlComponent, TranslocoModule, MatRadioModule],
  templateUrl: './address.component.html',
  providers: [AddressSearchStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressComponent implements OnInit {
  private readonly store = inject(FindMyAgentStore);
  private readonly googleMapsService = inject(GoogleMapsScriptService);

  protected readonly searchStore = inject(AddressSearchStore);

  ngOnInit(): void {
    this.googleMapsService.appendScript();
  }

  protected onLocationSelected(location: AutoCompleteResult, searchType: SearchType) {
    if (searchType === 'StreetAddress') {
      this.searchStore.addressSelectedEffect(location.name);
    } else {
      let locId = location.id;
      if (locId.includes('-')) {
        locId = locId.split('-')[1]; // handle region stubs (region-123)
      }

      this.searchStore.locationSelected({
        locationId: locId,
        localityName: location.rawName ?? '',
        locationType: this.mapToLocationType(location.autoSearchSubType),
      });
    }

    this.searchStore.selectedAddress$.pipe(take(1)).subscribe(({ addressId, address, locationId, localityName, locationType }) => {
      this.store.patchState({
        addressId,
        address,
        locationId: parseInt(locationId, 10),
        localityName,
        activeLocality: locationId,
        nextStep: 'important',
        locationType,
      });
    });
  }

  protected onSwitch(change: MatRadioChange) {
    this.searchStore.setSearchType(change.value);
  }

  private mapToLocationType(searchSubType: AutoSearchSubType | undefined) {
    switch (searchSubType?.toString()) {
      case 'City':
        return 'City';
      case 'Country':
        return 'Country';
      case 'County':
        return 'County';
      case 'Neighbourhood':
        return 'Neighbourhood';
      case 'State':
        return 'State';
      case 'Locality':
      default:
        return 'Locality';
    }
  }
}
